import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { getTeamsUsingPlayer, getAccountBySteamId } from "../../services/FantasyService"; // Import the account fetcher
import Loading from "../../components/Loading";
import { FantasyTeam } from "../../models/FantasyTeam";

const TeamsUsingPlayerPage = () => {
    const { steamID, season } = useParams<{ steamID: string; season: string }>();
    const [isLoading, setLoading] = useState(true);
    const [error, setError] = useState<string>('');
    const [teams, setTeams] = useState<FantasyTeam[]>([]);
    const [playerName, setPlayerName] = useState<string>('Unknown Player'); // State to store the player's name

    // Fetch player account details using steamID
    const fetchPlayerName = useCallback(async () => {
        try {
            const account = await getAccountBySteamId(steamID!); // Fetch the player's account data
            setPlayerName(account.playerName); // Set the player's name
        } catch (err) {
            setError("Failed to fetch player details");
        }
    }, [steamID]);

    const fetchTeams = useCallback(async () => {
        if (!steamID || !season) {
            setError("Invalid parameters.");
            setLoading(false);
            return;
        }

        try {
            const teams = await getTeamsUsingPlayer(steamID, Number(season));
            console.log("Fetched teams:", teams);

            // Sort teams by points (descending order)
            teams.sort((a, b) => b.points - a.points);

            setTeams(teams);
        } catch (err) {
            setError(err instanceof Error ? err.message : "An unexpected error occurred");
        } finally {
            setLoading(false);
        }
    }, [steamID, season]);

    // Fetch both player name and teams on component mount
    useEffect(() => {
        fetchPlayerName();  // Fetch the player's name
        fetchTeams();       // Fetch the teams
    }, [fetchPlayerName, fetchTeams]);

    if (isLoading) {
        return <Loading />;
    }

    if (error) {
        return <div className="text-center"><p style={{ color: '#e2e8f0' }}>{error}</p></div>;
    }

    if (!teams.length) {
        return <div className="text-center"><p style={{ color: '#e2e8f0' }}>No teams found for this player in this season.</p></div>;
    }

    return (
        <div className="container text-center">
            {/* Use the fetched player's name in the title */}
            <h1 style={{ color: '#e2e8f0' }}>Teams Using <span style={{ color: '#ff9933' }}>{playerName}</span> in Season {season}</h1>
            <div className="row">
                {teams.map((team) => (
                    <div className="col-12 col-md-4 mb-4" key={team.id}>
                        <div className="card p-3" style={{
                            background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.95) 0%, rgba(37, 37, 37, 0.95) 100%)',
                            borderRadius: '12px',
                            border: '1px solid rgba(255, 102, 0, 0.1)',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
                        }}>
                            {/* Display team owner info */}
                            <h5 style={{ color: '#e2e8f0', marginBottom: '12px' }}>
                                <span style={{ color: '#ff9933' }}>{team?.account?.playerName || 'Unknown'}'s</span> Team
                            </h5>
                            <p style={{ color: '#e2e8f0', fontSize: '0.9rem', marginBottom: '8px' }}>
                                Division: <span style={{ color: '#ff9933', fontWeight: '600' }}>{team.division}</span>
                            </p>
                            <p style={{ color: '#e2e8f0', fontSize: '1.1rem', fontWeight: '600', marginBottom: '16px' }}>
                                Points: <span style={{ color: '#ff9933' }}>{team.points}</span>
                            </p>
                            <Link 
                                to={`/fantasy/view/${team.account.steamID}/${team.season}/${team.division}`} 
                                className="btn w-100"
                                style={{
                                    background: 'rgba(255, 102, 0, 0.2)',
                                    color: '#ff9933',
                                    border: '1px solid rgba(255, 102, 0, 0.3)',
                                    borderRadius: '6px',
                                    padding: '8px 16px',
                                    fontWeight: '500',
                                    transition: 'background-color 0.2s ease, transform 0.2s ease',
                                    textDecoration: 'none'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = 'rgba(255, 102, 0, 0.3)';
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'rgba(255, 102, 0, 0.2)';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                }}
                            >
                                View Team
                            </Link>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default TeamsUsingPlayerPage;
